import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/shared/utils/utils";
import { CalendarProps } from "../extended/type";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

const Calendar = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<"div">, "onSelect"> & CalendarProps
>(
  (
    {
      className,
      // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars
      mode: _mode = "single",
      selected,
      onSelect,
      disabled,
      // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars
      initialFocus: _initialFocus,
      ...props
    },
    ref,
  ) => {
    const [currentMonth, setCurrentMonth] = React.useState(
      selected || new Date(),
    );

    React.useEffect(() => {
      if (!selected) {
        return;
      }

      setCurrentMonth((previousMonth) => {
        const isSameVisibleMonth =
          previousMonth.getMonth() === selected.getMonth() &&
          previousMonth.getFullYear() === selected.getFullYear();

        return isSameVisibleMonth ? previousMonth : selected;
      });
    }, [selected]);

    const { days, firstDay } = React.useMemo(() => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const firstDayOfMonth = new Date(year, month, 1).getDay();
      return { days: daysInMonth, firstDay: firstDayOfMonth };
    }, [currentMonth]);

    const handlePrevMonth = (): void => {
      setCurrentMonth(
        new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
      );
    };

    const handleNextMonth = (): void => {
      setCurrentMonth(
        new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
      );
    };

    const handleSelect = (day: number): void => {
      const newDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day,
      );
      if (disabled && disabled(newDate)) {
        return;
      }
      onSelect?.(newDate);
    };

    const isSelected = (day: number): boolean => {
      if (!selected) {
        return false;
      }
      return (
        selected.getDate() === day &&
        selected.getMonth() === currentMonth.getMonth() &&
        selected.getFullYear() === currentMonth.getFullYear()
      );
    };

    const isToday = (day: number): boolean => {
      const today = new Date();
      return (
        today.getDate() === day &&
        today.getMonth() === currentMonth.getMonth() &&
        today.getFullYear() === currentMonth.getFullYear()
      );
    };

    const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    const currentYear = new Date().getFullYear();

    const years = [...Array(101).keys()].map(
      (index) => currentYear - 100 + index,
    );

    const months = [...Array(12).keys()];

    return (
      <div
        className={`${cn("p-3", className)} w-min bg-white relative`}
        ref={ref}
        {...props}
      >
        <div className="flex justify-between items-center mb-4 space-x-4">
          <button
            type="button"
            onClick={handlePrevMonth}
            className={cn(
              buttonVariants({ variant: "outline" }),
              "h-7 w-7 bg-transparent p-0 flex justify-center items-center",
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {/* <div className="text-sm font-medium">
            {currentMonth.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </div> */}
          <div className="flex items-center gap-1.5">
            {/* Month */}
            <Select
              value={String(currentMonth.getMonth())}
              onValueChange={(value) =>
                setCurrentMonth(
                  new Date(currentMonth.getFullYear(), Number(value), 1),
                )
              }
            >
              <SelectTrigger className="w-18 border border-border rounded-md [&>span>span]:pr-2.5 min-h-7! px-2.5">
                <SelectValue />
              </SelectTrigger>

              <SelectContent
                position="popper"
                className="min-w-[var(--radix-select-trigger-width)]"
              >
                {months.map((monthIndex) => (
                  <SelectItem
                    key={monthIndex}
                    value={String(monthIndex)}
                    className="cursor-pointer"
                  >
                    {new Date(2000, monthIndex).toLocaleString("en-US", {
                      month: "short",
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Year */}
            <Select
              value={String(currentMonth.getFullYear())}
              onValueChange={(value) =>
                setCurrentMonth(
                  new Date(Number(value), currentMonth.getMonth(), 1),
                )
              }
            >
              <SelectTrigger className="w-20 border border-border rounded-md [&>span>span]:pr-2.5 min-h-7! px-2.5">
                <SelectValue />
              </SelectTrigger>

              <SelectContent
                position="popper"
                className="min-w-[var(--radix-select-trigger-width)]"
              >
                {years.map((year) => (
                  <SelectItem
                    key={year}
                    value={String(year)}
                    className="cursor-pointer"
                  >
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            type="button"
            onClick={handleNextMonth}
            className={cn(
              buttonVariants({ variant: "outline" }),
              "h-7 w-7 bg-transparent p-0 flex justify-center items-center",
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-muted-foreground w-9 font-normal text-[0.8rem]"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 place-items-center gap-1 text-center text-sm ">
          {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
          {Array.from({ length: firstDay }).map((_unused, i) => (
            <div key={`empty-${i}`} />
          ))}
          {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
          {Array.from({ length: days }).map((_unused, i) => {
            const day = i + 1;
            const date = new Date(
              currentMonth.getFullYear(),
              currentMonth.getMonth(),
              day,
            );
            const isDisabled = disabled ? disabled(date) : false;
            return (
              <button
                type="button"
                key={day}
                onClick={() => handleSelect(day)}
                disabled={isDisabled}
                className={` ${cn(
                  buttonVariants({ variant: "calendarDaysBtn" }),
                  "size-7 [&]:p-0 font-normal aria-selected:opacity-100",
                  isSelected(day) &&
                    "bg-primary text-pure-white hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                  !isSelected(day) &&
                    isToday(day) &&
                    "bg-accent text-accent-foreground",
                  isDisabled && "text-muted-foreground opacity-50",
                )} flex justify-center items-center`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    );
  },
);

Calendar.displayName = "Calendar";

export { Calendar };
